import axios from 'axios'
import router from '@router'
import config from '@src/config'

export const state = {
  accountId: null,
  basketId: null,
  braintreeToken: null,
  consent: null,
  emailAddress: null,
  itemQuantity: null,
  items: null,
  loading: null,
  outstandingBalanceToPay: null,
  paymentResult: null,
  selectedItems: null,
  selectedTotal: null,
  serviceCharge: null,
  splitBill: null,
  splitServiceCharge: null,
  tableNumber: null,
  tip: null,
  total: null,
}

export const getters = {
  getAccountId: (state) => {
    return state?.accountId
  },
  getBasketId: (state) => {
    return state?.basketId
  },
  getBraintreeToken: (state) => {
    return state?.braintreeToken
  },
  getConsent: (state) => {
    return state?.consent
  },
  getEmailAddress: (state) => {
    return state?.emailAddress
  },
  getItemQuantity: (state) => {
    return state?.itemQuantity
  },
  getItems: (state) => {
    return state?.items
  },
  getLoading: (state) => {
    return state?.loading
  },
  getOutstandingBalanceToPay: (state) => {
    return state?.outstandingBalanceToPay
  },
  getSelectedItems: (state) => {
    return state?.selectedItems
  },
  getSelectedTotal: (state) => {
    return state?.selectedTotal
  },
  getPaymentResult: (state) => {
    return state?.paymentResult
  },
  getServiceCharge: (state) => {
    return state?.serviceCharge
  },
  getSplitBill: (state) => {
    return state?.splitBill
  },
  getSplitServiceCharge: (state) => {
    return state?.splitServiceCharge
  },
  getTableNumber: (state) => {
    return state?.tableNumber
  },
  getTip: (state) => {
    return state?.tip
  },
  getTotal: (state) => {
    return state?.total
  },
}

export const mutations = {
  setAccountId(state, { accountId }) {
    state.accountId = accountId
  },
  setBasketId(state, { basketId }) {
    state.basketId = basketId
  },
  setBraintreeToken(state, { braintreeToken }) {
    state.braintreeToken = braintreeToken
  },
  setConsent(state, { consent }) {
    state.consent = consent
  },
  setEmailAddress(state, { emailAddress }) {
    state.emailAddress = emailAddress
  },
  setItemQuantity(state, { itemQuantity }) {
    state.itemQuantity = +itemQuantity
  },
  setItems(state, { items }) {
    state.items = items
  },
  setLoading(state, { loading }) {
    state.loading = loading
  },
  setOutstandingBalanceToPay(state, { outstandingBalanceToPay }) {
    state.outstandingBalanceToPay = outstandingBalanceToPay
  },
  setPaymentResult(state, { paymentResult }) {
    state.paymentResult = paymentResult
  },
  setSelectedItems(state, { selectedItems }) {
    state.selectedItems = selectedItems
  },
  setSelectedTotal(state, { selectedTotal }) {
    state.selectedTotal = selectedTotal
  },
  setServiceCharge(state, { serviceCharge }) {
    state.serviceCharge = serviceCharge
  },
  setSplitBill(state, { splitBill }) {
    state.splitBill = splitBill
  },
  setSplitServiceCharge(state, { splitServiceCharge }) {
    state.splitServiceCharge = splitServiceCharge
  },
  setTableNumber(state, { tableNumber }) {
    state.tableNumber = +tableNumber
  },
  setTip(state, { tip }) {
    state.tip = +tip
  },
  setTotal(state, { total }) {
    state.total = +total
  },
}

export const actions = {
  setConsent({ commit }, { consent }) {
    commit('setConsent', { consent })
  },

  setEmailAddress({ commit }, { emailAddress }) {
    commit('setEmailAddress', { emailAddress })
  },

  setLoading({ commit }, { loading }) {
    commit('setLoading', { loading })
  },

  setSelectedItems({ commit, getters }, { selectedItems }) {
    const items = getters.getItems
    const serviceCharge = getters.getServiceCharge

    const selectedItemsArray = selectedItems
      ? selectedItems.map((item) => items[item])
      : []

    const selectedTotal = selectedItemsArray.reduce((sum, item) => {
      const totalPrice = item.totalPrice ?? 0
      return sum + parseFloat(totalPrice)
    }, 0.0)

    // Even proportion of service charge
    const hasSelectedItems = selectedItems && selectedItems.length > 0
    const splitServiceCharge = hasSelectedItems
      ? (parseFloat(serviceCharge) / items.length) * selectedItems.length
      : null

    // // Percentage of the service charge
    // const percentage = selectedTotal / serviceCharge / 10
    // const splitServiceCharge = hasSelectedItems
    //   ? parseFloat(serviceCharge) * percentage
    //   : null

    commit('setSelectedTotal', { selectedTotal })
    commit('setSelectedItems', { selectedItems })
    commit('setSplitServiceCharge', { splitServiceCharge })
  },

  setTableNumber({ commit }, { tableNumber }) {
    commit('setTableNumber', { tableNumber })
  },

  setTip({ commit }, { tip }) {
    commit('setTip', { tip })
  },

  checkForData({ dispatch, getters }) {
    const items = getters.getItems
    if (!items || items.length < 1) {
      router.push('/').catch(() => {})
      dispatch('ui/showError', 'No table data was found, please try again', {
        root: true,
      })
    }
  },

  setSplitBill({ commit, state }, { splitBill, items }) {
    commit('setSplitBill', { splitBill })
    const itemsToPay = items || state.items

    commit('setTotal', {
      total: itemsToPay.reduce((acc, item) => {
        const totalPrice = item.totalPrice ?? 0
        return acc + totalPrice
      }, 0.0),
    })
  },

  async getBill({ commit, rootGetters }, { tableNumber }) {
    const location = rootGetters['location/getLocation']
    const siteId = location?.siteId
    const merchantId = location?.merchantId

    try {
      commit('setLoading', { loading: true })

      const salesAreaId = location?.salesAreaId

      const response = await axios.post(
        `${config.apiEndpoint}site/${siteId}/area/${salesAreaId}/table/${tableNumber}`,
        { merchantId }
      )

      const accountId = response?.data?.accountBreakdown?.account?.accountId
      const basketId = response?.data?.accountBreakdown?.account?.basketId
      const braintreeToken = response?.data?.braintreeToken?.clientToken
      const account = response?.data?.accountBreakdown?.account?.account

      const items = account
        // filter out items with no display name
        .filter((item) => !!item.display)
        // Add totalPrice and choices to the items array
        .map((item, index) => {
          // Map choiceLines and andedLines
          const choiceLines = [...(item?.choiceLines || [])].map(
            (choice, index) => ({
              id: index,
              name: choice?.display?.name,
              price: choice?.tariffPrice || 0,
              choices: [...(choice?.andedLines || [])].map((choice, index) => ({
                id: index,
                name: choice?.display?.name,
                price: choice?.tariffPrice || 0,
                choices: [...(choice?.andedLines || [])].map(
                  (choice, index) => ({
                    id: index,
                    name: choice?.display?.name,
                    price: choice?.tariffPrice || 0,
                  })
                ),
              })),
            })
          )

          // Map andedLines
          const andedLines = [...(item?.andedLines || [])].map(
            (choice, index) => ({
              id: index,
              name: choice?.display?.name,
              price: choice?.tariffPrice ?? 0,
              choices: [...(choice?.andedLines || [])].map((choice, index) => ({
                id: index,
                name: choice?.display?.name,
                price: choice?.tariffPrice ?? 0,
                choices: [...(choice?.andedLines || [])].map(
                  (choice, index) => ({
                    id: index,
                    name: choice?.display?.name,
                    price: choice?.tariffPrice ?? 0,
                  })
                ),
              })),
            })
          )

          // Concat choices
          const choices = [...choiceLines, ...andedLines]

          // Total up choiceLines + 2 levels of andedLines
          const choiceTotal = [...(item?.choiceLines || [])].reduce(
            (acc, item) => {
              const andedTotal = [...(item?.andedLines || [])].reduce(
                (acc, item) => {
                  const andedTotal = [...(item?.andedLines || [])].reduce(
                    (acc, item) => {
                      const tariffPrice = item?.tariffPrice ?? 0
                      const quantity = item?.quantity ?? 0
                      return acc + tariffPrice * quantity
                    },
                    0
                  )

                  const tariffPrice = item?.tariffPrice ?? 0
                  const quantity = item?.quantity ?? 0
                  return acc + tariffPrice * quantity + andedTotal
                },
                0
              )

              const tariffPrice = item?.tariffPrice ?? 0
              const quantity = item?.quantity ?? 0
              return acc + tariffPrice * quantity + andedTotal
            },
            0
          )

          // Total up andedLines
          const andedTotal =
            (item.andedLines &&
              [...(item.andedLines || [])].reduce((acc, item) => {
                return acc + item.tariffPrice * item.quantity
              }, 0)) ||
            0

          // Total
          const tariffPrice = item?.tariffPrice ?? 0
          const totalPrice = tariffPrice + choiceTotal + andedTotal

          return {
            ...item,
            price: item.tariffPrice,
            totalPrice,
            choices,
          }
        })

      const outstandingBalanceToPay =
        response?.data?.accountBreakdown?.account?.outstandingBalanceToPay

      const total = items.reduce((acc, item) => {
        const totalPrice = item?.totalPrice ?? 0
        const quantity = item?.quantity ?? 0
        return acc + totalPrice * quantity
      }, 0.0)

      const taxes =
        response?.data?.accountBreakdown?.account?.TaxesAndServiceCharges ?? []
      const serviceChargeLine = taxes.filter((item) => item.TaxId === 3)
      const serviceCharge = serviceChargeLine[0]?.Amount

      commit('setAccountId', { accountId })
      commit('setBasketId', { basketId })
      commit('setBraintreeToken', { braintreeToken })
      commit('setItems', { items })
      commit('setItemQuantity', { itemQuantity: items.length })
      commit('setLoading', { loading: false })
      commit('setOutstandingBalanceToPay', {
        outstandingBalanceToPay,
      })
      commit('setServiceCharge', { serviceCharge })
      commit('setTableNumber', { tableNumber })
      commit('setTotal', { total })
    } catch (error) {
      console.error(error)
      commit('setAccountId', { accountId: null })
      commit('setBasketId', { basketId: null })
      commit('setBraintreeToken', { braintreeToken: null })
      commit('setItems', { items: null })
      commit('setItemQuantity', { itemQuantity: null })
      commit('setLoading', { loading: false })
      commit('setOutstandingBalanceToPay', { outstandingBalanceToPay: null })
      commit('setSelectedItems', { selectedItems: null })
      commit('setSelectedTotal', { selectedTotal: null })
      commit('setServiceCharge', { serviceCharge: null })
      commit('setSplitServiceCharge', { splitServiceCharge: null })
      commit('setSplitBill', { splitBill: false })
      commit('setTableNumber', { tableNumber: null })
      commit('setTip', { tip: null })
      commit('setTotal', { total: null })
      router
        .push({ path: `/unavailable?location=${siteId}&table=${tableNumber}` })
        .catch(() => {})
    }
  },

  async makePayment({ commit, getters, rootGetters }, { nonce }) {
    try {
      commit('setLoading', { loading: true })

      const accountId = getters.getAccountId
      const amount = getters.getSelectedTotal || getters.getTotal
      const brandName = rootGetters['contentful/getBrand']?.brandId
      const basketId = getters.getBasketId
      const consent = getters.getConsent
      const customerEmail = getters.getEmailAddress
      const items = getters.getItems
      const location = rootGetters['location/getLocation']
      const merchantId = location?.merchantId
      const iOrderSiteId = location?.iOrderSiteId
      const salesAreaId = location?.salesAreaId
      const selectedItems = getters.getSelectedItems
      const siteId = location?.siteId
      const siteName = location?.name
      const tableNumber = getters.getTableNumber
      const tip = getters.getTip

      const itemsToPay = selectedItems
        ? items.filter((item, index) => selectedItems.includes(index))
        : items

      const body = {
        amount,
        brandName,
        consent,
        customerEmail,
        iOrderSiteId,
        items: itemsToPay,
        location,
        merchantId,
        nonce,
        siteName,
        tableNumber,
        tip,
      }

      // Remove `wantToOrderPromptVisible` from the payload
      delete body.location.wantToOrderPromptVisible

      const response = await axios.post(
        `${config.apiEndpoint}site/${siteId}/area/${salesAreaId}/account/${accountId}/basket/${basketId}/pay`,
        body
      )

      if (!response) throw new Error('No response from the server')

      router.push({ path: '/success' }).catch(() => {})

      commit('setLoading', { loading: false })
      return true
    } catch (error) {
      console.error(error)
      commit('setLoading', { loading: false })
      router.push({ path: '/failure' }).catch(() => {})
    }
  },
}
